import {
  Button,
  Card,
  Col,
  Divider,
  Input,
  Row,
  Space,
  Typography,
} from '@/components';
import { RobotOutlined, SendOutlined, UserOutlined } from '@ant-design/icons';
import type { KeyboardEvent } from 'react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAiChatStackSiderView } from '../_hooks/useAiChatStackSiderView';
import { useChatStream } from '../_hooks/useChatStream';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export const AiChatContent: FC = () => {
  const { t: _t } = useTranslation();
  const aiChatStackSiderView = useAiChatStackSiderView();
  const projectId = aiChatStackSiderView.lastItem?.config.projectId;

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { sendMessage } = useChatStream(projectId || '');

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading) {
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: '',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);

      const stream = await sendMessage([...messages, userMessage]);

      let accumulatedContent = '';

      for await (const chunk of stream) {
        accumulatedContent += chunk;
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessage.id
              ? { ...msg, content: accumulatedContent }
              : msg,
          ),
        );
      }
    } catch (_error) {
      // エラー時は最後のメッセージを削除
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, messages, sendMessage]);

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  const handleClearChat = useCallback(() => {
    setMessages([]);
  }, []);

  const renderMessage = useCallback((message: Message) => {
    const isUser = message.role === 'user';
    return (
      <div key={message.id} style={{ marginBottom: 16 }}>
        <Row gutter={8} align="top">
          <Col flex="none">
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: isUser ? '#1890ff' : '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isUser ? (
                <UserOutlined style={{ color: 'white' }} />
              ) : (
                <RobotOutlined style={{ color: '#666' }} />
              )}
            </div>
          </Col>
          <Col flex="auto">
            <Card
              size="small"
              bordered={false}
              style={{
                backgroundColor: isUser ? '#e6f7ff' : '#fafafa',
                marginLeft: 8,
              }}
            >
              <Text style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {message.content}
              </Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {message.timestamp.toLocaleTimeString()}
                </Text>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }, []);

  // プロジェクトIDが取得できない場合は何も表示しない
  if (!projectId) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
        }}
      >
        <Text type="secondary">プロジェクトが選択されていません</Text>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
            <RobotOutlined style={{ marginRight: 8 }} />
            AI アシスタント
          </Title>
          <Button size="small" onClick={handleClearChat}>
            クリア
          </Button>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            backgroundColor: '#fafafa',
            padding: 16,
            marginBottom: 16,
            borderRadius: 8,
          }}
        >
          {messages.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#666',
              }}
            >
              <RobotOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <Text type="secondary">
                この事業について何でもお気軽にお聞きください
              </Text>
            </div>
          ) : (
            <>
              {messages.map(renderMessage)}
              {isLoading && (
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <div
                    style={{
                      display: 'inline-block',
                      width: 16,
                      height: 16,
                      border: '2px solid #f3f3f3',
                      borderTop: '2px solid #1890ff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    AI が回答を生成しています...
                  </Text>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        <Divider style={{ margin: 0 }} />

        <div
          style={{
            padding: '16px 0',
            backgroundColor: 'white',
          }}
        >
          <Space.Compact style={{ width: '100%' }}>
            <TextArea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="メッセージを入力してください..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={isLoading}
              style={{ resize: 'none' }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              loading={isLoading}
            />
          </Space.Compact>
          <Text
            type="secondary"
            style={{ fontSize: 12, marginTop: 8, display: 'block' }}
          >
            Shift + Enter で改行、Enter で送信
          </Text>
        </div>
      </div>
    </>
  );
};
